/**
 * AdminPricingPlayground — Iter 69.
 *
 * Admin-only debug surface that calls `POST /api/admin/pricing-playground/quote`
 * and renders the full breakdown JSON live. Built so vendor-support can answer
 * "why does this booking show €X?" without writing SQL.
 *
 * Layout: form on the left, JSON/breakdown panel on the right. Inputs flow
 * downward so the operator can copy a renter's booking dates into the
 * datetime-locals, tweak rate rules, and see the new total appear inline.
 */
import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, Plus, Trash2, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const SEASONS = ["spring", "summer", "fall", "winter"];

const DEFAULT_MULTIPLIERS = { spring: 1.0, summer: 1.5, fall: 1.0, winter: 0.8 };

const todayIso = () => new Date().toISOString().slice(0, 16);
const inDaysIso = (d) => {
  const t = new Date();
  t.setDate(t.getDate() + d);
  return t.toISOString().slice(0, 16);
};

export default function AdminPricingPlayground() {
  const [basePrice, setBasePrice] = useState("50");
  const [start, setStart] = useState(inDaysIso(7));
  const [end, setEnd] = useState(inDaysIso(12));
  const [multipliers, setMultipliers] = useState(DEFAULT_MULTIPLIERS);
  const [rateRules, setRateRules] = useState([]);
  const [discountTiers, setDiscountTiers] = useState([]);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(false);
  // Iter 69 — "Load from booking ID" prefill.
  const [loadBookingId, setLoadBookingId] = useState("");
  const [loadedFromLabel, setLoadedFromLabel] = useState(null);

  const loadFromBooking = async () => {
    const id = loadBookingId.trim();
    if (!id) return;
    setWorking(true);
    try {
      const { data } = await api.get(`/admin/pricing-playground/from-booking/${encodeURIComponent(id)}`);
      setBasePrice(String(data.base_price));
      // Server returns ISO 8601; trim to the datetime-local "YYYY-MM-DDTHH:mm" shape.
      const toLocal = (iso) => (iso ? String(iso).slice(0, 16) : "");
      setStart(toLocal(data.start));
      setEnd(toLocal(data.end));
      const fullMults = { ...DEFAULT_MULTIPLIERS };
      Object.entries(data.multipliers || {}).forEach(([k, v]) => { fullMults[k] = Number(v); });
      setMultipliers(fullMults);
      setRateRules((data.rate_rules || []).map((r) => ({
        start_date: (r.start_date || "").slice(0, 10),
        end_date:   (r.end_date   || "").slice(0, 10),
        daily_price_eur: r.daily_price_eur || 0,
        name: r.name || "Rule",
        created_at: r.created_at || new Date().toISOString(),
      })));
      setDiscountTiers((data.discount_tiers || []).map((t) => ({
        min_days: Number(t.min_days) || 1,
        percent:  Number(t.percent)  || 0,
      })));
      setLoadedFromLabel(`${data.booking_label} — ${data.car_label} (${data.vendor_label})`);
      setQuote(null);
      toast.success(`Loaded ${data.car_label}.`);
    } catch (e) {
      const msg = formatApiError(e);
      toast.error(msg);
      setLoadedFromLabel(null);
    } finally {
      setWorking(false);
    }
  };

  const setMult = (s, v) =>
    setMultipliers((m) => ({ ...m, [s]: Number.isFinite(parseFloat(v)) ? parseFloat(v) : 1.0 }));

  const addRule = () =>
    setRateRules((r) => [
      ...r,
      {
        start_date: new Date().toISOString().slice(0, 10),
        end_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
        daily_price_eur: 80,
        name: "Override",
        created_at: new Date().toISOString(),
      },
    ]);
  const updateRule = (i, k, v) =>
    setRateRules((r) => r.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const removeRule = (i) => setRateRules((r) => r.filter((_, idx) => idx !== i));

  const addTier = () => setDiscountTiers((t) => [...t, { min_days: 7, percent: 10 }]);
  const updateTier = (i, k, v) =>
    setDiscountTiers((t) => t.map((x, idx) => (idx === i ? { ...x, [k]: parseFloat(v) || 0 } : x)));
  const removeTier = (i) => setDiscountTiers((t) => t.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError(null);
    setWorking(true);
    try {
      const payload = {
        base_price: parseFloat(basePrice) || 0,
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        multipliers,
        rate_rules: rateRules.length
          ? rateRules.map((r) => ({
              ...r,
              daily_price_eur: parseFloat(r.daily_price_eur) || 0,
            }))
          : undefined,
        discount_tiers: discountTiers.length ? discountTiers : undefined,
      };
      const { data } = await api.post("/admin/pricing-playground/quote", payload);
      setQuote(data);
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
      setQuote(null);
    } finally {
      setWorking(false);
    }
  };

  return (
    <Card data-testid="admin-pricing-playground">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-indigo-600" /> Pricing playground
        </CardTitle>
        <CardDescription>
          Punch in a base price + date range + rate rules + discount tiers and
          see the full breakdown live. Pure simulation — no DB writes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* ─── Inputs ─── */}
          <div className="space-y-5">
            {/* Iter 69 — Load from booking ID. Prefills every input below
                from a live booking so vendor-support can answer "why does
                booking #ABC123 show €X?" without manual data entry. */}
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
              <Label className="text-[10px] uppercase tracking-wider font-bold text-indigo-700">Load from booking ID</Label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  type="text"
                  placeholder="paste a booking ID (e.g. 7f3a2b1c-…)"
                  value={loadBookingId}
                  onChange={(e) => setLoadBookingId(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") loadFromBooking(); }}
                  className="text-xs font-mono"
                  data-testid="playground-load-booking-id"
                />
                <Button
                  type="button"
                  onClick={loadFromBooking}
                  disabled={working || !loadBookingId.trim()}
                  variant="outline"
                  className="rounded-lg gap-1.5 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                  data-testid="playground-load-booking-submit"
                >
                  <Download className="h-3.5 w-3.5" /> Load
                </Button>
              </div>
              {loadedFromLabel && (
                <div className="mt-1.5 text-[11px] text-emerald-700 font-semibold" data-testid="playground-loaded-from">
                  Loaded: {loadedFromLabel}
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Base €/day</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={basePrice}
                  onChange={(e) => setBasePrice(e.target.value)}
                  data-testid="playground-base-price"
                />
              </div>
              <div>
                <Label className="text-xs">Pick-up</Label>
                <Input
                  type="datetime-local"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  data-testid="playground-start"
                />
              </div>
              <div>
                <Label className="text-xs">Return</Label>
                <Input
                  type="datetime-local"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  data-testid="playground-end"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Seasonal multipliers</Label>
              <div className="grid grid-cols-4 gap-2 mt-1.5">
                {SEASONS.map((s) => (
                  <div key={s} className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500">{s}</span>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      value={multipliers[s]}
                      onChange={(e) => setMult(s, e.target.value)}
                      className="text-sm"
                      data-testid={`playground-multiplier-${s}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs">Date-range rate rules</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addRule} className="h-7 px-2 text-xs gap-1" data-testid="playground-add-rule">
                  <Plus className="h-3 w-3" /> Add rule
                </Button>
              </div>
              {rateRules.length === 0 ? (
                <p className="text-[11px] text-neutral-400 italic">None — pricing uses seasonal multipliers only.</p>
              ) : (
                <ul className="space-y-2">
                  {rateRules.map((r, i) => (
                    <li key={`rule-${i}`} className="grid grid-cols-[1fr_1fr_90px_30px] gap-2 items-end" data-testid={`playground-rule-${i}`}>
                      <Input type="date" value={r.start_date} onChange={(e) => updateRule(i, "start_date", e.target.value)} className="text-xs" />
                      <Input type="date" value={r.end_date} onChange={(e) => updateRule(i, "end_date", e.target.value)} className="text-xs" />
                      <Input type="number" step="0.5" value={r.daily_price_eur} onChange={(e) => updateRule(i, "daily_price_eur", e.target.value)} className="text-xs" placeholder="€/day" />
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeRule(i)} className="h-9 w-9 text-rose-600 hover:bg-rose-50" data-testid={`playground-rule-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs">Discount tiers</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addTier} className="h-7 px-2 text-xs gap-1" data-testid="playground-add-tier">
                  <Plus className="h-3 w-3" /> Add tier
                </Button>
              </div>
              {discountTiers.length === 0 ? (
                <p className="text-[11px] text-neutral-400 italic">None — no length-of-rental discount applied.</p>
              ) : (
                <ul className="space-y-2">
                  {discountTiers.map((t, i) => (
                    <li key={`tier-${i}`} className="grid grid-cols-[1fr_1fr_30px] gap-2 items-end" data-testid={`playground-tier-${i}`}>
                      <div>
                        <span className="text-[10px] text-neutral-500">Min days</span>
                        <Input type="number" min="1" value={t.min_days} onChange={(e) => updateTier(i, "min_days", e.target.value)} className="text-xs" />
                      </div>
                      <div>
                        <span className="text-[10px] text-neutral-500">% off</span>
                        <Input type="number" min="0" max="100" step="0.5" value={t.percent} onChange={(e) => updateTier(i, "percent", e.target.value)} className="text-xs" />
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeTier(i)} className="h-9 w-9 text-rose-600 hover:bg-rose-50" data-testid={`playground-tier-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Button
              onClick={submit}
              disabled={working}
              className="w-full rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700"
              data-testid="playground-submit"
            >
              {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
              Compute quote
            </Button>
          </div>

          {/* ─── Output ─── */}
          <div className="space-y-3">
            <Label className="text-xs">Result</Label>
            {error && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="playground-error">
                {error}
              </div>
            )}
            {!quote && !error && (
              <div className="rounded-xl border border-dashed border-border bg-neutral-50/60 p-6 text-center text-neutral-400 text-sm" data-testid="playground-empty">
                Click "Compute quote" to simulate a booking.
              </div>
            )}
            {quote && (
              <div className="space-y-3" data-testid="playground-result">
                <div className="grid grid-cols-3 gap-3">
                  <Metric label="Total" value={`€${quote.quote.total.toFixed(2)}`} accent="text-emerald-700 bg-emerald-50 ring-emerald-100" testId="playground-total" />
                  <Metric label="Days" value={quote.quote.days} accent="text-indigo-700 bg-indigo-50 ring-indigo-100" testId="playground-days" />
                  {quote.quote.discount_amount > 0 ? (
                    <Metric
                      label={`Saved ${quote.quote.discount_percent}%`}
                      value={`–€${quote.quote.discount_amount.toFixed(2)}`}
                      accent="text-rose-700 bg-rose-50 ring-rose-100"
                      testId="playground-discount"
                    />
                  ) : (
                    <Metric label="Discount" value="—" accent="text-neutral-500 bg-neutral-100 ring-neutral-200" testId="playground-discount-empty" />
                  )}
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-1.5">Breakdown</h4>
                  <ul className="space-y-1.5">
                    {Object.entries(quote.quote.breakdown).map(([k, v]) => (
                      <li key={k} className="flex items-center justify-between text-sm border-b border-neutral-100 py-1.5" data-testid={`playground-breakdown-${k}`}>
                        <span className="capitalize">{k === "custom_rate" ? "Custom rate (rules)" : k}</span>
                        <span className="font-mono text-xs text-neutral-600">
                          {v.days} × €{v.rate.toFixed(2)} = <strong className="text-neutral-900">€{v.subtotal.toFixed(2)}</strong>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <details className="text-xs">
                  <summary className="cursor-pointer text-neutral-500 hover:text-neutral-700">Raw JSON</summary>
                  <pre className="mt-2 p-3 bg-neutral-900 text-neutral-100 rounded-lg overflow-auto max-h-80" data-testid="playground-json">
                    {JSON.stringify(quote, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, accent, testId }) {
  return (
    <div className={`rounded-xl p-3 ring-1 ${accent}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">{label}</div>
      <div className="text-lg font-display font-semibold mt-0.5">{value}</div>
    </div>
  );
}
